import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CreditCard,
  Landmark,
  Receipt,
  Zap,
  Briefcase,
  Gift,
  ShoppingBag,
  Sparkles,
  Star,
  Network,
  Folder,
  Tag,
  Trash2,
  Plus,
  Search,
  LayoutGrid,
  List as ListIcon,
  RefreshCw,
  MoreVertical,
  Edit3,
  Copy,
  Share2,
  History,
  Lock,
  Paperclip,
  RotateCcw,
  Check,
  Eye,
  EyeOff,
  Clock,
  ArrowUpDown,
  Filter,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Download,
  Calendar,
  ExternalLink,
  X,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useApplications } from '../../hooks/useApplications';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../../crypto/WebCryptoProvider';
import api from '../../api/client';
import { copyTextToClipboard } from '../../utils/clipboard';

// Modals
import CardTemplatesModal, { CARD_TEMPLATES } from '../../components/cards/CardTemplatesModal';
import CardEditorModal, { detectCardNetwork, CARD_TYPES, CARD_THEMES } from '../../components/cards/CardEditorModal';
import CardViewModal from '../../components/cards/CardViewModal';
import CardActivityModal from '../../components/cards/CardActivityModal';
import {
  MoveCardCategoryModal,
  ManageCardTagsModal,
  DeleteCardModal,
} from '../../components/cards/CardActionModals';
import { ShareVaultItemModal } from '../../components/ShareVaultItemModal';

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const DEFAULT_CATEGORIES = [
  { id: 'cat-finance', name: 'Finance', icon: '💳' },
  { id: 'cat-personal', name: 'Personal', icon: '👤' },
  { id: 'cat-business', name: 'Business', icon: '💼' },
  { id: 'cat-travel', name: 'Travel', icon: '✈️' },
  { id: 'cat-shopping', name: 'Shopping', icon: '🛍️' },
  { id: 'cat-subs', name: 'Online Subscriptions', icon: '⚡' },
];

export default function CardsApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { vaultKey, user } = useAuth();
  const { hasPermission } = useApplications();

  // URL query params: all | favorites | credit | debit | prepaid | virtual | shared-with-me | shared-by-me | categories | trash
  const searchParams = new URLSearchParams(location.search);
  const activeFilter = searchParams.get('filter') || 'all';
  const urlCategory = searchParams.get('category') || '';
  const cardIdParam = searchParams.get('cardId');

  const vaultCrypto = useMemo(() => new VaultCrypto(WebCryptoProvider), []);

  // State
  const [vaultId, setVaultId] = useState(null);
  const [cards, setCards] = useState([]);
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem('ciphervault_card_categories');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) {}
    }
    return DEFAULT_CATEGORIES;
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  // Controls
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(urlCategory || 'all');
  const [expiryFilter, setExpiryFilter] = useState('all'); // 'all' | 'valid' | 'expiring_soon' | 'expired'
  const [sortBy, setSortBy] = useState('updated_desc');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('ciphervault_cards_view') || 'grid';
  });
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // Card-level sensitive reveal state (map of cardId -> boolean)
  const [revealedIds, setRevealedIds] = useState({});
  const cardHideTimersRef = useRef({});

  // Active modal targets
  const [viewingCard, setViewingCard] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [activityCard, setActivityCard] = useState(null);
  const [shareCardTarget, setShareCardTarget] = useState(null);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [moveToCategoryTarget, setMoveToCategoryTarget] = useState(null);
  const [manageTagsTarget, setManageTagsTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    const mq = window.matchMedia('(max-width: 768px)');
    if (mq.addEventListener) {
      mq.addEventListener('change', handleResize);
    } else if (mq.addListener) {
      mq.addListener(handleResize);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mq.removeEventListener) {
        mq.removeEventListener('change', handleResize);
      } else if (mq.removeListener) {
        mq.removeListener(handleResize);
      }
    };
  }, []);

  // Copied indicator
  const [copiedId, setCopiedId] = useState(null);

  // Floating Action Button (FAB) state: Tap to add card, Hold to select template
  const [showFabTooltip, setShowFabTooltip] = useState(true);
  const [holdProgress, setHoldProgress] = useState(0);

  const pressTimerRef = useRef(null);
  const holdProgressIntervalRef = useRef(null);
  const holdStartTimeRef = useRef(0);
  const isLongPressTriggeredRef = useRef(false);

  const handleOpenAddCard = useCallback(() => {
    setEditingCard(null);
    setIsEditorOpen(true);
  }, []);

  const handleOpenTemplates = useCallback(() => {
    setTemplatesModalOpen(true);
  }, []);

  const startHold = (e) => {
    holdStartTimeRef.current = Date.now();
    isLongPressTriggeredRef.current = false;
    setHoldProgress(0);

    const startTime = Date.now();
    const duration = 380; // ms to activate hold

    if (holdProgressIntervalRef.current) clearInterval(holdProgressIntervalRef.current);
    holdProgressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(100, Math.round((elapsed / duration) * 100));
      setHoldProgress(pct);
      if (pct >= 100) {
        clearInterval(holdProgressIntervalRef.current);
      }
    }, 25);

    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      isLongPressTriggeredRef.current = true;
      setHoldProgress(100);
      if (navigator.vibrate) navigator.vibrate([60, 40, 60]);
      handleOpenTemplates();
    }, duration);
  };

  const cancelOrFinishHold = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (holdProgressIntervalRef.current) {
      clearInterval(holdProgressIntervalRef.current);
      holdProgressIntervalRef.current = null;
    }
    setHoldProgress(0);

    const elapsed = Date.now() - holdStartTimeRef.current;
    if (!isLongPressTriggeredRef.current && elapsed < 380) {
      if (navigator.vibrate) navigator.vibrate(30);
      handleOpenAddCard();
    }
    isLongPressTriggeredRef.current = false;
  };

  const cancelHoldOnly = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (holdProgressIntervalRef.current) {
      clearInterval(holdProgressIntervalRef.current);
      holdProgressIntervalRef.current = null;
    }
    setHoldProgress(0);
    isLongPressTriggeredRef.current = false;
  };

  // Permissions
  const canCreate = hasPermission('cards', 'create') ?? true;
  const canEdit = hasPermission('cards', 'edit') ?? true;
  const canDelete = hasPermission('cards', 'delete') ?? true;
  const canShare = hasPermission('cards', 'share') ?? true;

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  }, []);

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('ciphervault_cards_view', mode);
  };

  // Close menus on outside click
  useEffect(() => {
    const handleClick = () => setActiveMenuId(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
      if (holdProgressIntervalRef.current) clearInterval(holdProgressIntervalRef.current);
      Object.values(cardHideTimersRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  // Toggle card-level sensitive number reveal with 15s timer
  const toggleRevealCard = (id) => {
    setRevealedIds((prev) => {
      const current = Boolean(prev[id]);
      if (cardHideTimersRef.current[id]) {
        clearTimeout(cardHideTimersRef.current[id]);
      }

      if (!current) {
        cardHideTimersRef.current[id] = setTimeout(() => {
          setRevealedIds((p) => ({ ...p, [id]: false }));
        }, 15000);
        return { ...prev, [id]: true };
      } else {
        return { ...prev, [id]: false };
      }
    });
  };

  // ─────────────────────────────────────────────
  // Load & Decrypt Cards
  // ─────────────────────────────────────────────
  const loadCards = useCallback(async () => {
    if (!vaultKey) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');

    try {
      const vaultsRes = await api.get('/vaults');
      const v = vaultsRes.data?.[0];
      if (!v) {
        setLoading(false);
        return;
      }
      setVaultId(v.id);

      const itemsRes = await api.get(`/vaults/${v.id}/items`);
      const allItems = itemsRes.data || [];

      // Filter payment card items
      const rawCardItems = allItems.filter(
        (item) =>
          item.item_type === 'PAYMENT_CARD' ||
          item.item_type === 'payment_card' ||
          item.item_type === 'CREDIT_CARD' ||
          item.item_type === 'credit_card' ||
          item.item_type === 'CARD' ||
          item.item_type === 'card'
      );

      const decrypted = await Promise.all(
        rawCardItems.map(async (item) => {
          try {
            const jsonText = await vaultCrypto.decryptItem(
              item.encrypted_data,
              vaultKey,
              item.id
            );
            const parsed = JSON.parse(jsonText);
            return {
              id: item.id,
              vault_id: item.vault_id,
              status: item.status || 'ACTIVE',
              is_favorite: Boolean(item.is_favorite),
              category_id: item.category_id,
              created_at: item.created_at,
              updated_at: item.updated_at,
              ...parsed,
            };
          } catch (decryptErr) {
            console.error(`Failed to decrypt card ${item.id}:`, decryptErr);
            return {
              id: item.id,
              vault_id: item.vault_id,
              cardName: 'Decryption Error',
              status: item.status || 'ACTIVE',
              is_favorite: Boolean(item.is_favorite),
              decryptionFailed: true,
              fields: {},
            };
          }
        })
      );

      setCards(decrypted);
    } catch (err) {
      console.error('Failed to load cards:', err);
      setLoadError('Failed to load encrypted cards. Please verify your connection.');
    } finally {
      setLoading(false);
    }
  }, [vaultKey, vaultCrypto]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  // Handle cardId query param
  useEffect(() => {
    if (cardIdParam && cards.length > 0) {
      const match = cards.find((c) => c.id === cardIdParam);
      if (match) setViewingCard(match);
    }
  }, [cardIdParam, cards]);

  // Sync category filter
  useEffect(() => {
    if (urlCategory) setCategoryFilter(urlCategory);
  }, [urlCategory]);

  // ─────────────────────────────────────────────
  // CRUD Actions
  // ─────────────────────────────────────────────

  // Save Card (Create or Update)
  const handleSaveCard = async (payload) => {
    if (!vaultId || !vaultKey) return;

    try {
      const isUpdating = Boolean(editingCard?.id);
      const cardId = isUpdating ? editingCard.id : generateUuid();
      const now = new Date().toISOString();

      const existingVersions = editingCard?.versions || [];
      const newVersion = {
        version: existingVersions.length + 1,
        savedAt: now,
        title: payload.cardName,
        summary: isUpdating ? 'Payment card updated & re-encrypted' : 'Initial payment card record created',
        fields: {
          issuer: payload.fields?.issuer,
          cardholderName: payload.fields?.cardholderName,
          cardLast4: (payload.fields?.cardNumber || '').replace(/\D/g, '').slice(-4),
          expiry: `${payload.fields?.expiryMonth || 'MM'}/${payload.fields?.expiryYear || 'YYYY'}`,
        },
      };

      const existingAudit = editingCard?.auditLog || [];
      const newAudit = {
        action: isUpdating ? 'Updated' : 'Created',
        actor: 'You (Current Device)',
        timestamp: now,
        details: isUpdating
          ? 'Card details and billing information re-encrypted'
          : 'Secure encrypted payment card created',
      };

      const fullCardObj = {
        ...editingCard,
        ...payload,
        id: cardId,
        vault_id: vaultId,
        status: editingCard?.status || 'ACTIVE',
        is_favorite: editingCard?.is_favorite || false,
        created_at: editingCard?.created_at || now,
        updated_at: now,
        versions: [...existingVersions, newVersion],
        auditLog: [newAudit, ...existingAudit],
      };

      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(fullCardObj),
        vaultKey,
        cardId
      );

      if (isUpdating) {
        await api.put(`/vaults/${vaultId}/items/${cardId}`, {
          id: cardId,
          itemType: 'PAYMENT_CARD',
          encryptedData,
          isFavorite: fullCardObj.is_favorite,
          status: fullCardObj.status,
        });
        setCards((prev) => prev.map((c) => (c.id === cardId ? fullCardObj : c)));
        if (viewingCard?.id === cardId) setViewingCard(fullCardObj);
        showToast('Payment card updated & re-encrypted');
      } else {
        await api.post(`/vaults/${vaultId}/items`, {
          id: cardId,
          itemType: 'PAYMENT_CARD',
          encryptedData,
        });
        setCards((prev) => [fullCardObj, ...prev]);
        showToast('New payment card created & encrypted');
      }
    } catch (err) {
      console.error('Failed to save card:', err);
      showToast('Failed to save card: ' + (err.message || 'Server error'));
      throw err;
    }
  };

  // Toggle Favorite
  const handleToggleFavorite = async (target) => {
    if (!vaultId || !vaultKey) return;
    const newFav = !target.is_favorite;
    const updated = { ...target, is_favorite: newFav };

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'PAYMENT_CARD',
        encryptedData,
        isFavorite: newFav,
        status: target.status,
      });

      setCards((prev) => prev.map((c) => (c.id === target.id ? updated : c)));
      if (viewingCard?.id === target.id) setViewingCard(updated);
      showToast(newFav ? 'Added to Favorites' : 'Removed from Favorites');
    } catch (err) {
      showToast('Failed to update favorite status');
    }
  };

  // Duplicate Card
  const handleDuplicateCard = async (target) => {
    if (!canCreate || !vaultId || !vaultKey) return;
    try {
      const newId = generateUuid();
      const now = new Date().toISOString();
      const duplicated = {
        ...target,
        id: newId,
        cardName: `[Copy] ${target.cardName || target.title || 'Untitled Card'}`,
        title: `[Copy] ${target.cardName || target.title || 'Untitled Card'}`,
        is_favorite: false,
        status: 'ACTIVE',
        created_at: now,
        updated_at: now,
        versions: [
          {
            version: 1,
            savedAt: now,
            title: `[Copy] ${target.cardName || target.title}`,
            summary: 'Duplicated from existing card',
            fields: { ...target.fields },
          },
        ],
        auditLog: [
          {
            action: 'Created',
            actor: 'You (Current Device)',
            timestamp: now,
            details: `Cloned from payment card "${target.cardName || target.title}"`,
          },
        ],
      };

      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(duplicated),
        vaultKey,
        newId
      );
      await api.post(`/vaults/${vaultId}/items`, {
        id: newId,
        itemType: 'PAYMENT_CARD',
        encryptedData,
      });
      setCards((prev) => [duplicated, ...prev]);
      showToast('Card duplicated successfully');
    } catch (err) {
      showToast('Failed to duplicate card');
    }
  };

  // Move to Category
  const handleMoveToCategory = async (target, newCategory) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      category: newCategory,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'PAYMENT_CARD',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setCards((prev) => prev.map((c) => (c.id === target.id ? updated : c)));
      if (viewingCard?.id === target.id) setViewingCard(updated);
      showToast(`Moved to category "${newCategory}"`);
    } catch (err) {
      showToast('Failed to update category');
    }
  };

  // Manage Tags
  const handleSaveTags = async (target, newTags) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      tags: newTags,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'PAYMENT_CARD',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setCards((prev) => prev.map((c) => (c.id === target.id ? updated : c)));
      if (viewingCard?.id === target.id) setViewingCard(updated);
      showToast('Tags updated successfully');
    } catch (err) {
      showToast('Failed to update tags');
    }
  };

  // Move to Trash (Soft Delete)
  const handleMoveToTrash = async (target) => {
    if (!canDelete || !vaultId || !vaultKey) return;
    const updated = {
      ...target,
      status: 'TRASHED',
      trashed_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'PAYMENT_CARD',
        encryptedData,
        isFavorite: target.is_favorite,
        status: 'TRASHED',
      });
      setCards((prev) => prev.map((c) => (c.id === target.id ? updated : c)));
      if (viewingCard?.id === target.id) setViewingCard(null);
      showToast('Card moved to Trash');
    } catch (err) {
      showToast('Failed to move card to Trash');
    }
  };

  // Restore from Trash
  const handleRestoreFromTrash = async (target) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      status: 'ACTIVE',
      trashed_at: null,
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'PAYMENT_CARD',
        encryptedData,
        isFavorite: target.is_favorite,
        status: 'ACTIVE',
      });
      setCards((prev) => prev.map((c) => (c.id === target.id ? updated : c)));
      showToast('Card restored to active vault');
    } catch (err) {
      showToast('Failed to restore card');
    }
  };

  // Permanent Delete
  const handlePermanentDelete = async (target) => {
    if (!canDelete || !vaultId) return;
    try {
      await api.delete(`/vaults/${vaultId}/items/${target.id}`);
      setCards((prev) => prev.filter((c) => c.id !== target.id));
      if (viewingCard?.id === target.id) setViewingCard(null);
      showToast('Payment card permanently erased');
    } catch (err) {
      showToast('Failed to permanently delete card');
    }
  };

  // Empty Trash
  const handleEmptyTrash = async () => {
    if (!canDelete || !vaultId) return;
    const trashed = cards.filter((c) => c.status === 'TRASHED');
    if (trashed.length === 0) return;
    try {
      await Promise.all(
        trashed.map((c) => api.delete(`/vaults/${vaultId}/items/${c.id}`))
      );
      setCards((prev) => prev.filter((c) => c.status !== 'TRASHED'));
      showToast('Trash emptied successfully');
    } catch (err) {
      showToast('Failed to empty trash');
    }
  };

  // Rollback version
  const handleRestoreVersion = async (target, versionObj) => {
    if (!vaultId || !vaultKey) return;
    const now = new Date().toISOString();
    const updated = {
      ...target,
      cardName: versionObj.title || target.cardName,
      title: versionObj.title || target.title,
      updated_at: now,
      auditLog: [
        {
          action: 'Restored',
          actor: 'You',
          timestamp: now,
          details: `Restored version snapshot v${versionObj.version}`,
        },
        ...(target.auditLog || []),
      ],
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'PAYMENT_CARD',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setCards((prev) => prev.map((c) => (c.id === target.id ? updated : c)));
      if (viewingCard?.id === target.id) setViewingCard(updated);
      showToast(`Restored version v${versionObj.version}`);
    } catch (err) {
      showToast('Failed to restore version');
    }
  };

  // ─────────────────────────────────────────────
  // Expiry Calculations & Stats
  // ─────────────────────────────────────────────
  const getCardExpiry = (month, year) => {
    if (!month || !year) return { status: 'none', label: 'No Expiry', daysRemaining: Infinity };
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const expY = parseInt(year, 10);
    const expM = parseInt(month, 10);

    if (isNaN(expY) || isNaN(expM)) return { status: 'none', label: 'Invalid Date', daysRemaining: Infinity };

    if (expY < currentYear || (expY === currentYear && expM < currentMonth)) {
      return { status: 'expired', label: 'Expired', color: '#ef4444', daysRemaining: -1 };
    }
    if (expY === currentYear && expM === currentMonth) {
      return { status: 'expiring_soon', label: 'Expires this month!', color: '#f59e0b', daysRemaining: 15 };
    }
    if (expY === currentYear && expM === currentMonth + 1) {
      return { status: 'expiring_soon', label: 'Expiring next month', color: '#f59e0b', daysRemaining: 45 };
    }

    return { status: 'valid', label: 'Active', color: '#10b981', daysRemaining: 365 };
  };

  const expiryStats = useMemo(() => {
    let expiredCount = 0;
    let expiringSoonCount = 0;

    cards.forEach((c) => {
      if (c.status === 'TRASHED') return;
      const f = c.fields || c;
      const info = getCardExpiry(f.expiryMonth, f.expiryYear);
      if (info.status === 'expired') expiredCount++;
      else if (info.status === 'expiring_soon') expiringSoonCount++;
    });

    return { expiredCount, expiringSoonCount };
  }, [cards]);

  // Filtered Cards
  const filteredCards = useMemo(() => {
    return cards.filter((c) => {
      // Trash filter
      if (activeFilter === 'trash') {
        return c.status === 'TRASHED';
      }
      if (c.status === 'TRASHED') return false;

      // Sidebar route filters
      if (activeFilter === 'favorites' && !c.is_favorite) return false;
      if (activeFilter === 'credit' && c.cardType !== 'credit') return false;
      if (activeFilter === 'debit' && c.cardType !== 'debit') return false;
      if (activeFilter === 'prepaid' && c.cardType !== 'prepaid') return false;
      if (activeFilter === 'virtual' && c.cardType !== 'virtual') return false;

      if (activeFilter === 'shared-with-me') {
        const userEmail = (user?.email || '').toLowerCase();
        const isShared = Array.isArray(c.shares) && c.shares.some((s) => s.email === userEmail);
        if (!isShared) return false;
      }
      if (activeFilter === 'shared-by-me') {
        if (!Array.isArray(c.shares) || c.shares.length === 0) return false;
      }

      // Dropdown Type filter
      if (typeFilter !== 'all') {
        if (c.cardType !== typeFilter) return false;
      }

      // Dropdown Category filter
      if (categoryFilter !== 'all') {
        if ((c.category || '').toLowerCase() !== categoryFilter.toLowerCase()) return false;
      }

      // Dropdown Expiry filter
      if (expiryFilter !== 'all') {
        const f = c.fields || c;
        const info = getCardExpiry(f.expiryMonth, f.expiryYear);
        if (expiryFilter === 'expired' && info.status !== 'expired') return false;
        if (expiryFilter === 'expiring_soon' && info.status !== 'expiring_soon') return false;
        if (expiryFilter === 'valid' && (info.status === 'expired' || info.status === 'expiring_soon')) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const f = c.fields || c;
        const nameMatch = (c.cardName || c.title || '').toLowerCase().includes(q);
        const issuerMatch = (f.issuer || '').toLowerCase().includes(q);
        const holderMatch = (f.cardholderName || '').toLowerCase().includes(q);
        const numMatch = (f.cardNumber || '').replace(/\s+/g, '').includes(q.replace(/\s+/g, ''));
        const catMatch = (c.category || '').toLowerCase().includes(q);
        const tagMatch = Array.isArray(c.tags) && c.tags.some((t) => t.toLowerCase().includes(q));

        if (!nameMatch && !issuerMatch && !holderMatch && !numMatch && !catMatch && !tagMatch) {
          return false;
        }
      }

      return true;
    });
  }, [cards, activeFilter, typeFilter, categoryFilter, expiryFilter, searchQuery, user]);

  // Sorted Cards
  const sortedCards = useMemo(() => {
    const list = [...filteredCards];
    return list.sort((a, b) => {
      if (sortBy === 'title_asc') {
        return (a.cardName || a.title || '').localeCompare(b.cardName || b.title || '');
      }
      if (sortBy === 'title_desc') {
        return (b.cardName || b.title || '').localeCompare(a.cardName || a.title || '');
      }
      if (sortBy === 'created_desc') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sortBy === 'expiry_soonest') {
        const fa = a.fields || a;
        const fb = b.fields || b;
        const dateA = new Date(parseInt(fa.expiryYear || '2099', 10), parseInt(fa.expiryMonth || '12', 10), 1);
        const dateB = new Date(parseInt(fb.expiryYear || '2099', 10), parseInt(fb.expiryMonth || '12', 10), 1);
        return dateA - dateB;
      }
      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    });
  }, [filteredCards, sortBy]);

  // Currently active card for action menu
  const activeMenuCard = useMemo(() => {
    if (!activeMenuId) return null;
    return sortedCards.find((c) => c.id === activeMenuId) || null;
  }, [activeMenuId, sortedCards]);

  // Card Action Menu Content (shared between desktop dropdown & mobile portal sheet)
  const renderCardActionMenu = (item) => {
    const f = item.fields || item;
    const network = detectCardNetwork(f.cardNumber);
    const isRevealed = Boolean(revealedIds[item.id]);

    return (
      <>
        <div className="card-menu-header">
          <div className="card-menu-title">{item.cardName || item.title || 'Payment Card'}</div>
          <div className="card-menu-sub">{f.issuer || 'Card'} • {network.name}</div>
        </div>
        <div className="identity-menu-header-divider" />
        {activeFilter === 'trash' ? (
          <>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                handleRestoreFromTrash(item);
              }}
            >
              <RotateCcw size={15} />
              <span>Restore Card</span>
            </button>
            <button
              type="button"
              className="dropdown-item text-danger"
              onClick={() => {
                setActiveMenuId(null);
                setDeleteTarget({ card: item, isPermanent: true });
              }}
            >
              <Trash2 size={15} />
              <span>Erase Forever</span>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                setViewingCard(item);
              }}
            >
              <Eye size={15} />
              <span>Open / View Details</span>
            </button>
            {canEdit && (
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setActiveMenuId(null);
                  setEditingCard(item);
                  setIsEditorOpen(true);
                }}
              >
                <Edit3 size={15} />
                <span>Edit Card</span>
              </button>
            )}
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                toggleRevealCard(item.id);
              }}
            >
              {isRevealed ? <EyeOff size={15} /> : <Eye size={15} />}
              <span>{isRevealed ? 'Hide Secure Details' : 'Reveal Secure Details'}</span>
            </button>
            {f.cardNumber && (
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setActiveMenuId(null);
                  copyTextToClipboard(f.cardNumber.replace(/\s+/g, ''));
                  showToast('Card number copied. Sensitive data will clear in 30s.');
                }}
              >
                <Copy size={15} />
                <span>Copy Card Number</span>
              </button>
            )}
            {f.expiryMonth && f.expiryYear && (
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setActiveMenuId(null);
                  copyTextToClipboard(`${f.expiryMonth}/${f.expiryYear}`);
                  showToast('Expiry date copied');
                }}
              >
                <Copy size={15} />
                <span>Copy Expiry Date</span>
              </button>
            )}
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                handleToggleFavorite(item);
              }}
            >
              <Star size={15} />
              <span>{item.is_favorite ? 'Remove Favorite' : 'Add to Favorites'}</span>
            </button>
            {canCreate && (
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setActiveMenuId(null);
                  handleDuplicateCard(item);
                }}
              >
                <Copy size={15} />
                <span>Duplicate Card</span>
              </button>
            )}
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                setMoveToCategoryTarget(item);
              }}
            >
              <Folder size={15} />
              <span>Move to Category</span>
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                setManageTagsTarget(item);
              }}
            >
              <Tag size={15} />
              <span>Manage Tags</span>
            </button>
            {canShare && (
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setActiveMenuId(null);
                  setShareCardTarget(item);
                }}
              >
                <Share2 size={15} />
                <span>Share Card</span>
              </button>
            )}
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                setActivityCard(item);
              }}
            >
              <History size={15} />
              <span>View Activity</span>
            </button>
            {canDelete && (
              <button
                type="button"
                className="dropdown-item text-danger"
                onClick={() => {
                  setActiveMenuId(null);
                  setDeleteTarget({ card: item, isPermanent: false });
                }}
              >
                <Trash2 size={15} />
                <span>Move to Trash</span>
              </button>
            )}
          </>
        )}
        <div className="card-menu-cancel-row">
          <button
            type="button"
            className="card-menu-cancel-btn"
            onClick={() => setActiveMenuId(null)}
          >
            Cancel
          </button>
        </div>
      </>
    );
  };

  // Header Title & Meta according to active filter
  const getHeaderInfo = () => {
    switch (activeFilter) {
      case 'favorites':
        return {
          title: 'Favorite Cards',
          subtitle: 'Starred and prioritized credit and debit cards for rapid access.',
          icon: Star,
          color: '#f59e0b',
        };
      case 'credit':
        return {
          title: 'Credit Cards',
          subtitle: 'Rewards, travel, cash-back, and line of credit payment cards.',
          icon: CreditCard,
          color: '#3b82f6',
        };
      case 'debit':
        return {
          title: 'Debit Cards',
          subtitle: 'Checking account and ATM debit cards for daily payments and withdrawals.',
          icon: Landmark,
          color: '#10b981',
        };
      case 'prepaid':
        return {
          title: 'Prepaid Cards',
          subtitle: 'Reloadable travel debit cards, fixed budgets, and allowance cards.',
          icon: Receipt,
          color: '#f97316',
        };
      case 'virtual':
        return {
          title: 'Virtual Cards',
          subtitle: 'Disposable burner cards and merchant-locked online subscription cards.',
          icon: Zap,
          color: '#8b5cf6',
        };
      case 'shared-with-me':
        return {
          title: 'Shared With Me',
          subtitle: 'Payment cards and corporate expense accounts shared with your profile.',
          icon: CreditCard,
          color: '#06b6d4',
        };
      case 'shared-by-me':
        return {
          title: 'Shared By Me',
          subtitle: 'Payment cards you have authorized for team members or family members.',
          icon: Network,
          color: '#10b981',
        };
      case 'categories':
        return {
          title: 'Card Categories',
          subtitle: 'Organize payment cards by Finance, Personal, Business, Travel, and Subscriptions.',
          icon: Folder,
          color: '#f59e0b',
        };
      case 'trash':
        return {
          title: 'Card Trash',
          subtitle: 'Deleted payment cards. Restore them anytime or permanently erase cryptographic keys.',
          icon: Trash2,
          color: '#ef4444',
        };
      default:
        return {
          title: 'All Cards',
          subtitle: 'Credit, debit, prepaid, and virtual payment cards.',
          icon: CreditCard,
          color: '#3b82f6',
        };
    }
  };

  const headerInfo = getHeaderInfo();
  const HeaderIcon = headerInfo.icon;

  const getMaskedCardNumber = (raw) => {
    if (!raw) return '•••• •••• •••• ••••';
    const clean = raw.replace(/\D/g, '');
    const last4 = clean.slice(-4) || '••••';
    return `•••• •••• •••• ${last4}`;
  };

  return (
    <MainLayout>
      {/* Toast notice */}
      {toastMessage && (
        <div className="notes-toast animate-slide-down">
          <CheckCircle2 size={16} color="#10b981" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Header Banner - Full Bleed Edge-to-Edge with Multicolor Gradient */}
      <div className="notes-module-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="notes-header-left">
            <div
              className="notes-app-icon-badge notes-hero-icon-badge"
              style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}
            >
              <HeaderIcon size={24} />
            </div>
            <div>
              <div className="notes-header-title-row">
                <h1 className="notes-page-title notes-hero-title">{headerInfo.title}</h1>
              </div>
              <p className="notes-page-subtitle notes-hero-subtitle">{headerInfo.subtitle}</p>
            </div>
          </div>

          <div className="notes-header-actions">
            {activeFilter === 'trash' ? (
              <button
                type="button"
                className="btn btn-danger notes-hero-btn-danger"
                onClick={handleEmptyTrash}
                disabled={filteredCards.length === 0}
              >
                <Trash2 size={15} />
                <span>Empty Trash</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary notes-hero-btn-secondary"
                  onClick={() => setTemplatesModalOpen(true)}
                  title="Choose from 8 ready-made card templates"
                >
                  <Sparkles size={16} color="#fde047" />
                  <span>Templates</span>
                </button>

                {canCreate && (
                  <button
                    type="button"
                    className="btn btn-primary notes-hero-btn-primary"
                    onClick={() => {
                      setEditingCard(null);
                      setIsEditorOpen(true);
                    }}
                  >
                    <Plus size={16} />
                    <span>Add Card</span>
                  </button>
                )}
              </>
            )}

            <button
              type="button"
              className="btn-icon notes-refresh-btn notes-hero-btn-secondary"
              onClick={loadCards}
              title="Refresh and sync payment cards"
            >
              <RefreshCw size={17} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="notes-module-container cards-module-container">

        {/* Expiry Alert Banner */}
        {activeFilter !== 'trash' && (expiryStats.expiredCount > 0 || expiryStats.expiringSoonCount > 0) && (
          <div className="identity-expiry-alert-banner">
            <div className="identity-expiry-alert-left">
              <AlertTriangle size={20} className="text-warning" />
              <div>
                <strong>Payment Card Expiration Alert</strong>
                <p>
                  {expiryStats.expiredCount > 0 && `${expiryStats.expiredCount} card has expired. `}
                  {expiryStats.expiringSoonCount > 0 &&
                    `${expiryStats.expiringSoonCount} card(s) approaching expiration. `}
                  Please verify with your issuing bank or update renewal information.
                </p>
              </div>
            </div>
            <div className="identity-expiry-alert-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setExpiryFilter(expiryStats.expiredCount > 0 ? 'expired' : 'expiring_soon')}
              >
                View Expiring Cards
              </button>
            </div>
          </div>
        )}

        {/* Categories View */}
        {activeFilter === 'categories' ? (
          <div className="identity-categories-view">
            <div className="identity-categories-header">
              <h3>Card Categories</h3>
              <p className="text-secondary text-sm">
                Browse payment cards grouped by purpose and finance classification.
              </p>
            </div>

            <div className="identity-categories-grid">
              {categories.map((cat) => {
                const count = cards.filter(
                  (c) => c.status !== 'TRASHED' && (c.category || '').toLowerCase() === cat.name.toLowerCase()
                ).length;
                return (
                  <div
                    key={cat.id || cat.name}
                    className="identity-category-card glass-panel"
                    onClick={() => {
                      setCategoryFilter(cat.name);
                      navigate(`/apps/cards?category=${encodeURIComponent(cat.name)}`);
                    }}
                  >
                    <div className="identity-cat-icon">{cat.icon || '💳'}</div>
                    <div className="identity-cat-info">
                      <h4>{cat.name}</h4>
                      <span className="text-secondary text-sm">{count} Cards</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Normal Dashboard / Filtered Cards View */
          <>
            {/* Mobile One-Row Icon Filter Bar (shown only on mobile <= 640px) */}
            <div className="cards-mobile-filter-bar">
              {/* 1. Search Icon Button */}
              <button
                type="button"
                className={`cards-mobile-icon-btn ${searchQuery || isMobileSearchOpen ? 'is-active' : ''}`}
                onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                title="Search cards"
                aria-label="Search cards"
              >
                <Search size={17} />
                {searchQuery && <span className="cards-filter-dot" />}
              </button>

              {/* 2. Card Type Filter Icon Button */}
              <div
                className={`cards-mobile-icon-btn ${typeFilter !== 'all' ? 'is-active' : ''}`}
                title={`Type: ${typeFilter === 'all' ? 'All' : typeFilter}`}
              >
                <CreditCard size={17} />
                {typeFilter !== 'all' && <span className="cards-filter-dot" />}
                <select
                  className="cards-filter-hidden-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label="Filter by Card Type"
                >
                  <option value="all">All Card Types</option>
                  <option value="credit">Credit Cards</option>
                  <option value="debit">Debit Cards</option>
                  <option value="prepaid">Prepaid Cards</option>
                  <option value="virtual">Virtual Cards</option>
                  <option value="business">Business Cards</option>
                  <option value="gift">Gift Cards</option>
                  <option value="store">Store Cards</option>
                  <option value="custom">Custom Cards</option>
                </select>
              </div>

              {/* 3. Category Filter Icon Button */}
              <div
                className={`cards-mobile-icon-btn ${categoryFilter !== 'all' ? 'is-active' : ''}`}
                title={`Category: ${categoryFilter === 'all' ? 'All' : categoryFilter}`}
              >
                <Tag size={17} />
                {categoryFilter !== 'all' && <span className="cards-filter-dot" />}
                <select
                  className="cards-filter-hidden-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Filter by Category"
                >
                  <option value="all">All Categories</option>
                  <option value="Finance">Finance</option>
                  <option value="Personal">Personal</option>
                  <option value="Business">Business</option>
                  <option value="Travel">Travel</option>
                  <option value="Shopping">Shopping</option>
                  <option value="Online Subscriptions">Online Subscriptions</option>
                  {categories
                    .filter(
                      (c) =>
                        ![
                          'Finance',
                          'Personal',
                          'Business',
                          'Travel',
                          'Shopping',
                          'Online Subscriptions',
                        ].includes(c.name)
                    )
                    .map((c) => (
                      <option key={c.id || c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* 4. Expiry Filter Icon Button */}
              <div
                className={`cards-mobile-icon-btn ${expiryFilter !== 'all' ? 'is-active' : ''}`}
                title={`Expiry: ${expiryFilter === 'all' ? 'All' : expiryFilter}`}
              >
                <Calendar size={17} />
                {expiryFilter !== 'all' && <span className="cards-filter-dot" />}
                <select
                  className="cards-filter-hidden-select"
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                  aria-label="Filter by Expiry"
                >
                  <option value="all">All Expiry States</option>
                  <option value="valid">Active Cards</option>
                  <option value="expiring_soon">Expiring Soon</option>
                  <option value="expired">Expired Cards</option>
                </select>
              </div>

              {/* 5. Sort Icon Button */}
              <div
                className={`cards-mobile-icon-btn ${sortBy !== 'updated_desc' ? 'is-active' : ''}`}
                title="Sort cards"
              >
                <ArrowUpDown size={17} />
                {sortBy !== 'updated_desc' && <span className="cards-filter-dot" />}
                <select
                  className="cards-filter-hidden-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort Cards"
                >
                  <option value="updated_desc">Recently Updated</option>
                  <option value="title_asc">Card Name (A - Z)</option>
                  <option value="title_desc">Card Name (Z - A)</option>
                  <option value="expiry_soonest">Expiry: Soonest First</option>
                  <option value="created_desc">Creation Date</option>
                </select>
              </div>

              {/* 6. View Mode Toggle */}
              <button
                type="button"
                className="cards-mobile-icon-btn"
                onClick={() => toggleViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                title={viewMode === 'grid' ? 'Switch to List View' : 'Switch to Grid View'}
                aria-label={viewMode === 'grid' ? 'Switch to List View' : 'Switch to Grid View'}
              >
                {viewMode === 'grid' ? <LayoutGrid size={17} /> : <ListIcon size={17} />}
              </button>

              {/* 7. Reset Filters (only shown when any filter is active) */}
              {(searchQuery || typeFilter !== 'all' || categoryFilter !== 'all' || expiryFilter !== 'all' || sortBy !== 'updated_desc') && (
                <button
                  type="button"
                  className="cards-mobile-icon-btn cards-mobile-reset-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setTypeFilter('all');
                    setCategoryFilter('all');
                    setExpiryFilter('all');
                    setSortBy('updated_desc');
                    setIsMobileSearchOpen(false);
                  }}
                  title="Reset all filters"
                  aria-label="Reset all filters"
                >
                  <RotateCcw size={15} />
                </button>
              )}
            </div>

            {/* Mobile Expandable Search Row */}
            {isMobileSearchOpen && (
              <div className="cards-mobile-search-expand animate-slide-down">
                <Search size={15} className="cards-mobile-search-inner-icon" />
                <input
                  type="text"
                  autoFocus
                  placeholder="Search cards, issuer, last 4..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="cards-mobile-search-input"
                />
                <button
                  type="button"
                  className="cards-mobile-search-close"
                  onClick={() => {
                    setSearchQuery('');
                    setIsMobileSearchOpen(false);
                  }}
                >
                  <X size={15} />
                </button>
              </div>
            )}

            {/* Mobile Active Filter Badges */}
            {(searchQuery || typeFilter !== 'all' || categoryFilter !== 'all' || expiryFilter !== 'all') && (
              <div className="cards-mobile-active-badges">
                {searchQuery && (
                  <span className="cards-active-badge">
                    "{searchQuery}"
                    <button type="button" onClick={() => setSearchQuery('')}><X size={11} /></button>
                  </span>
                )}
                {typeFilter !== 'all' && (
                  <span className="cards-active-badge">
                    {typeFilter}
                    <button type="button" onClick={() => setTypeFilter('all')}><X size={11} /></button>
                  </span>
                )}
                {categoryFilter !== 'all' && (
                  <span className="cards-active-badge">
                    {categoryFilter}
                    <button type="button" onClick={() => setCategoryFilter('all')}><X size={11} /></button>
                  </span>
                )}
                {expiryFilter !== 'all' && (
                  <span className="cards-active-badge">
                    {expiryFilter.replace('_', ' ')}
                    <button type="button" onClick={() => setExpiryFilter('all')}><X size={11} /></button>
                  </span>
                )}
                <button
                  type="button"
                  className="cards-clear-all-text-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setTypeFilter('all');
                    setCategoryFilter('all');
                    setExpiryFilter('all');
                    setSortBy('updated_desc');
                  }}
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Desktop Filter Toolbar (hidden on <= 640px) */}
            <div className="notes-toolbar cards-desktop-toolbar">
              <div className="notes-toolbar-left">
                <div className="notes-search-box">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    className="notes-search-input"
                    placeholder="Search cards, issuer, cardholder, last 4 digits..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="notes-search-clear"
                      onClick={() => setSearchQuery('')}
                    >
                      &times;
                    </button>
                  )}
                </div>

                {/* Type Filter */}
                <select
                  className="notes-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Card Types</option>
                  <option value="credit">Credit Cards</option>
                  <option value="debit">Debit Cards</option>
                  <option value="prepaid">Prepaid Cards</option>
                  <option value="virtual">Virtual Cards</option>
                  <option value="business">Business Cards</option>
                  <option value="gift">Gift Cards</option>
                  <option value="store">Store Cards</option>
                  <option value="custom">Custom Cards</option>
                </select>

                {/* Category Filter */}
                <select
                  className="notes-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="Finance">Finance</option>
                  <option value="Personal">Personal</option>
                  <option value="Business">Business</option>
                  <option value="Travel">Travel</option>
                  <option value="Shopping">Shopping</option>
                  <option value="Online Subscriptions">Online Subscriptions</option>
                  {categories
                    .filter(
                      (c) =>
                        ![
                          'Finance',
                          'Personal',
                          'Business',
                          'Travel',
                          'Shopping',
                          'Online Subscriptions',
                        ].includes(c.name)
                    )
                    .map((c) => (
                      <option key={c.id || c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                </select>

                {/* Expiry Filter */}
                <select
                  className="notes-select"
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                >
                  <option value="all">All Expiry States</option>
                  <option value="valid">Active Cards</option>
                  <option value="expiring_soon">Expiring Soon</option>
                  <option value="expired">Expired Cards</option>
                </select>
              </div>

              <div className="notes-toolbar-right">
                {/* Sort */}
                <select
                  className="notes-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="updated_desc">Recently Updated</option>
                  <option value="title_asc">Card Name (A - Z)</option>
                  <option value="title_desc">Card Name (Z - A)</option>
                  <option value="expiry_soonest">Expiry: Soonest First</option>
                  <option value="created_desc">Creation Date</option>
                </select>

                {/* View Mode Toggle */}
                <div className="notes-view-mode-switch">
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('grid')}
                    title="Digital Card Grid"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('list')}
                    title="List View"
                  >
                    <ListIcon size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content */}
            {loading ? (
              <div className="notes-loading-state">
                <RefreshCw size={28} className="animate-spin text-primary" />
                <p>Decrypting payment cards...</p>
              </div>
            ) : loadError ? (
              <div className="alert alert-danger" style={{ margin: '20px 0' }}>
                <AlertCircle size={16} />
                <span>{loadError}</span>
              </div>
            ) : sortedCards.length === 0 ? (
              <div className="notes-empty-state glass-panel">
                <CreditCard size={56} opacity={0.3} />
                <h3>No Payment Cards Found</h3>
                <p>
                  {searchQuery || typeFilter !== 'all' || categoryFilter !== 'all' || expiryFilter !== 'all'
                    ? 'No cards match your current search and filter options.'
                    : activeFilter === 'trash'
                    ? 'The trash is currently empty.'
                    : 'Securely store credit, debit, virtual, and corporate cards in your vault.'}
                </p>
                {activeFilter !== 'trash' && canCreate && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setTemplatesModalOpen(true)}
                    >
                      <Sparkles size={16} />
                      <span>Use Template</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setEditingCard(null);
                        setIsEditorOpen(true);
                      }}
                    >
                      <Plus size={16} />
                      <span>Add Card</span>
                    </button>
                  </div>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              /* Digital Card Grid View */
              <div className="identity-cards-grid card-mockups-grid">
                {sortedCards.map((item) => {
                  const f = item.fields || item;
                  const network = detectCardNetwork(f.cardNumber);
                  const theme = f.theme || item.theme || 'sapphire';
                  const expiryInfo = getCardExpiry(f.expiryMonth, f.expiryYear);
                  const isRevealed = Boolean(revealedIds[item.id]);
                  const docCount = Array.isArray(item.attachments) ? item.attachments.length : 0;
                  const shareCount = Array.isArray(item.shares) ? item.shares.length : 0;

                  return (
                    <div
                      key={item.id}
                      className={`card-item-wrapper ${item.is_favorite ? 'is-favorite' : ''} ${activeMenuId === item.id ? 'menu-active' : ''}`}
                    >
                      {/* Realistic Digital Card Mockup */}
                      <div
                        className={`digital-card-hero theme-${theme}`}
                        onClick={() => setViewingCard(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-hero-header">
                          <span className="card-hero-issuer">{f.issuer || 'PAYMENT CARD'}</span>
                          <span className="card-hero-network-badge">{network.name}</span>
                        </div>

                        <div className="card-hero-chip-row">
                          <div className="card-hero-emv-chip"></div>
                          <div className="card-hero-contactless">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>

                        <div className="card-hero-number">
                          {isRevealed
                            ? f.cardNumber || '—'
                            : getMaskedCardNumber(f.cardNumber)}
                        </div>

                        <div className="card-hero-footer">
                          <div style={{ minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                            <span className="card-hero-sublabel">CARDHOLDER</span>
                            <span className="card-hero-holder-name">
                              {f.cardholderName?.toUpperCase() || 'CARDHOLDER'}
                            </span>
                          </div>
                          <div style={{ flexShrink: 0, textAlign: 'right', marginLeft: '8px' }}>
                            <span className="card-hero-sublabel">EXPIRES</span>
                            <span className="card-hero-exp-date">
                              {f.expiryMonth || 'MM'}/{(f.expiryYear || 'YYYY').slice(-2)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Card Controls & Meta Tray */}
                      <div className="card-item-controls-tray">
                        <div className="card-tray-left">
                          <span className="card-tray-name" title={item.cardName || item.title}>
                            {item.cardName || item.title}
                          </span>
                          <div className="card-tray-badges">
                            <span className="badge badge-secondary">{item.category || 'Finance'}</span>
                            {expiryInfo.status !== 'none' && (
                              <span
                                className="identity-expiry-status-pill"
                                style={{
                                  backgroundColor: `${expiryInfo.color}15`,
                                  color: expiryInfo.color,
                                  borderColor: `${expiryInfo.color}35`,
                                }}
                              >
                                {expiryInfo.label}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="card-tray-right">
                          {f.cardNumber && (
                            <>
                              <button
                                type="button"
                                className="btn-icon btn-sm"
                                onClick={() => toggleRevealCard(item.id)}
                                title={isRevealed ? 'Hide card number' : 'Reveal card number (15s)'}
                              >
                                {isRevealed ? <EyeOff size={15} /> : <Eye size={15} />}
                              </button>
                              <button
                                type="button"
                                className="btn-icon btn-sm"
                                onClick={() => {
                                  copyTextToClipboard(f.cardNumber.replace(/\s+/g, ''));
                                  setCopiedId(item.id);
                                  showToast('Card number copied. Sensitive data will clear in 30s.');
                                  setTimeout(() => setCopiedId(null), 2000);
                                }}
                                title="Copy card number"
                              >
                                {copiedId === item.id ? (
                                  <Check size={14} className="text-success" />
                                ) : (
                                  <Copy size={14} />
                                )}
                              </button>
                            </>
                          )}

                          {activeFilter !== 'trash' && (
                            <button
                              type="button"
                              className={`btn-icon btn-sm ${item.is_favorite ? 'text-warning' : ''}`}
                              onClick={() => handleToggleFavorite(item)}
                              title={item.is_favorite ? 'Remove Favorite' : 'Mark Favorite'}
                            >
                              <Star size={15} fill={item.is_favorite ? 'currentColor' : 'none'} />
                            </button>
                          )}

                          {/* 3-Dot Action Menu */}
                          <div className="notes-card-menu-relative cards-card-menu-anchor">
                            <button
                              type="button"
                              className="btn-icon btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.innerWidth <= 768) setIsMobile(true);
                                setActiveMenuId(activeMenuId === item.id ? null : item.id);
                              }}
                            >
                              <MoreVertical size={15} />
                            </button>

                            {!isMobile && activeMenuId === item.id && (
                              <>
                                <div
                                  className="card-menu-backdrop"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenuId(null);
                                  }}
                                />
                                <div
                                  className="notes-dropdown-menu animate-scale-in"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {renderCardActionMenu(item, false)}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Table List View */
              <div className="identity-list-container glass-panel">
                <table className="identity-table">
                  <thead>
                    <tr>
                      <th>Payment Card</th>
                      <th>Cardholder</th>
                      <th>Network</th>
                      <th>Card Number</th>
                      <th>Category</th>
                      <th>Expiry</th>
                      <th>Scans</th>
                      <th>Last Updated</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCards.map((item) => {
                      const f = item.fields || item;
                      const network = detectCardNetwork(f.cardNumber);
                      const expiryInfo = getCardExpiry(f.expiryMonth, f.expiryYear);
                      const isRevealed = Boolean(revealedIds[item.id]);
                      const docCount = Array.isArray(item.attachments) ? item.attachments.length : 0;

                      return (
                        <tr key={item.id} onClick={() => setViewingCard(item)}>
                          <td>
                            <div className="table-identity-cell">
                              <div
                                className="table-icon-box"
                                style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}
                              >
                                <CreditCard size={16} />
                              </div>
                              <div>
                                <div className="table-title">{item.cardName || item.title}</div>
                                <div className="table-type-sub">{f.issuer || 'Card Issuer'}</div>
                              </div>
                            </div>
                          </td>
                          <td>{f.cardholderName || '—'}</td>
                          <td>
                            <span className="badge badge-primary">{network.name}</span>
                          </td>
                          <td>
                            <div
                              className="table-docnum-cell"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>
                                {isRevealed ? f.cardNumber || '—' : getMaskedCardNumber(f.cardNumber)}
                              </span>
                              {f.cardNumber && (
                                <button
                                  type="button"
                                  className="btn-icon btn-sm"
                                  onClick={() => toggleRevealCard(item.id)}
                                  title={isRevealed ? 'Hide' : 'Reveal for 15s'}
                                >
                                  {isRevealed ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="badge badge-secondary">{item.category || 'Finance'}</span>
                          </td>
                          <td>
                            {expiryInfo.status !== 'none' ? (
                              <span
                                className="identity-expiry-status-pill"
                                style={{
                                  backgroundColor: `${expiryInfo.color}15`,
                                  color: expiryInfo.color,
                                  borderColor: `${expiryInfo.color}35`,
                                }}
                              >
                                {f.expiryMonth}/{f.expiryYear?.slice(-2)} ({expiryInfo.label})
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            {docCount > 0 ? (
                              <span className="meta-item">
                                <Paperclip size={13} />
                                <span>{docCount}</span>
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            {new Date(item.updated_at || item.created_at).toLocaleDateString([], {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </td>
                          <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <button
                                type="button"
                                className="btn-icon btn-sm"
                                onClick={() => setViewingCard(item)}
                                title="View Details"
                              >
                                <Eye size={15} />
                              </button>
                              {canEdit && (
                                <button
                                  type="button"
                                  className="btn-icon btn-sm"
                                  onClick={() => {
                                    setEditingCard(item);
                                    setIsEditorOpen(true);
                                  }}
                                  title="Edit Card"
                                >
                                  <Edit3 size={15} />
                                </button>
                              )}
                              <button
                                type="button"
                                className={`btn-icon btn-sm ${item.is_favorite ? 'text-warning' : ''}`}
                                onClick={() => handleToggleFavorite(item)}
                                title={item.is_favorite ? 'Remove Favorite' : 'Mark Favorite'}
                              >
                                <Star size={15} fill={item.is_favorite ? 'currentColor' : 'none'} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ───────────────────────────────────────────── */}
        {/* Modals */}
        {/* ───────────────────────────────────────────── */}

        {/* Templates Modal */}
        <CardTemplatesModal
          open={templatesModalOpen}
          onClose={() => setTemplatesModalOpen(false)}
          onSelectTemplate={(template) => {
            setTemplatesModalOpen(false);
            setEditingCard({
              cardName: template.fields?.cardName || template.defaultName || '',
              title: template.fields?.cardName || template.defaultName || '',
              cardType: template.cardType || 'credit',
              category: template.category || 'Finance',
              tags: template.tags || [],
              theme: template.theme || 'sapphire',
              fields: { ...template.fields },
              attachments: [],
              isLocked: false,
              lockPin: '',
              shares: [],
            });
            setIsEditorOpen(true);
          }}
        />

        {/* Editor Modal */}
        <CardEditorModal
          open={isEditorOpen}
          card={editingCard}
          categories={categories}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingCard(null);
          }}
          onSave={handleSaveCard}
          hasEditPermission={canEdit}
          onOpenTemplates={() => {
            setIsEditorOpen(false);
            setTemplatesModalOpen(true);
          }}
        />

        {/* Viewer Modal */}
        <CardViewModal
          open={Boolean(viewingCard)}
          card={viewingCard}
          onClose={() => setViewingCard(null)}
          onEdit={(cardObj) => {
            setEditingCard(cardObj);
            setIsEditorOpen(true);
          }}
          onToggleFavorite={handleToggleFavorite}
          onDelete={(cardObj) => {
            setViewingCard(null);
            setDeleteTarget({ card: cardObj, isPermanent: false });
          }}
          onShare={(cardObj) => {
            setShareCardTarget(cardObj);
          }}
          hasEditPermission={canEdit}
        />

        {/* Activity & Version History Modal */}
        <CardActivityModal
          open={Boolean(activityCard)}
          card={activityCard}
          onClose={() => setActivityCard(null)}
          onRestoreVersion={handleRestoreVersion}
        />

        {/* Move to Category Modal */}
        <MoveCardCategoryModal
          open={Boolean(moveToCategoryTarget)}
          card={moveToCategoryTarget}
          categories={categories}
          onClose={() => setMoveToCategoryTarget(null)}
          onSave={handleMoveToCategory}
        />

        {/* Manage Tags Modal */}
        <ManageCardTagsModal
          open={Boolean(manageTagsTarget)}
          card={manageTagsTarget}
          onClose={() => setManageTagsTarget(null)}
          onSave={handleSaveTags}
        />

        {/* Delete Card Modal */}
        <DeleteCardModal
          open={Boolean(deleteTarget)}
          card={deleteTarget?.card}
          isPermanent={deleteTarget?.isPermanent}
          onClose={() => setDeleteTarget(null)}
          onConfirm={(target) => {
            if (deleteTarget?.isPermanent) {
              handlePermanentDelete(target);
            } else {
              handleMoveToTrash(target);
            }
          }}
        />

        {/* Standard Vault Sharing Modal */}
        {shareCardTarget && (
          <ShareVaultItemModal
            isOpen={Boolean(shareCardTarget)}
            onClose={() => setShareCardTarget(null)}
            item={{
              id: shareCardTarget.id,
              name: shareCardTarget.cardName || shareCardTarget.title,
            }}
            onShared={() => {
              showToast('Payment card shared successfully');
            }}
          />
        )}

        {/* ══════════════════════════════════════════════════════════════
            BOTTOM-RIGHT FLOATING ACTION BUTTON (iOS / CIPHERVAULT FAB)
            - Tap to add card
            - Hold to select template
            ══════════════════════════════════════════════════════════════ */}
        <div
          className="v-fab-container cards-fab-container"
          role="region"
          aria-label="Quick Card Actions"
          onContextMenu={(e) => e.preventDefault()}
        >
          {showFabTooltip && (
            <div
              className="v-fab-tooltip animate-pop-in"
              onClick={handleOpenAddCard}
              title="Tap to add card, hold to select template"
            >
              <span className="v-fab-tooltip-icon">💳</span>
              <div className="v-fab-tooltip-text">
                <strong>Tap to add card</strong>
                <span style={{ fontSize: '0.72rem', opacity: 0.85, display: 'block' }}>Hold for template</span>
              </div>
              <button
                type="button"
                className="v-fab-tooltip-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFabTooltip(false);
                }}
                aria-label="Close hint"
              >
                <X size={12} />
              </button>
            </div>
          )}

          <button
            type="button"
            className={`v-fab-button v-fab-cards-btn ${holdProgress > 0 ? 'is-holding' : ''}`}
            onMouseDown={startHold}
            onMouseUp={cancelOrFinishHold}
            onMouseLeave={cancelHoldOnly}
            onTouchStart={startHold}
            onTouchEnd={cancelOrFinishHold}
            onTouchCancel={cancelHoldOnly}
            title="Tap to Add Card • Hold for Templates"
            aria-label="Tap to add card, hold to select template"
            style={{
              borderRadius: '50%',
              WebkitBorderRadius: '50%',
              width: '58px',
              height: '58px',
              minWidth: '58px',
              minHeight: '58px',
              maxWidth: '58px',
              maxHeight: '58px',
              aspectRatio: '1 / 1',
              overflow: 'hidden',
              padding: 0,
              position: 'relative',
            }}
          >
            {/* Radial hold progress indicator ring */}
            {holdProgress > 0 && (
              <svg
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  transform: 'rotate(-90deg)',
                  pointerEvents: 'none',
                }}
              >
                <circle
                  cx="29"
                  cy="29"
                  r="26"
                  stroke="rgba(255, 255, 255, 0.85)"
                  strokeWidth="3.5"
                  fill="none"
                  strokeDasharray="163.36"
                  strokeDashoffset={163.36 - (163.36 * holdProgress) / 100}
                  strokeLinecap="round"
                />
              </svg>
            )}

            <div className="v-fab-icon-wrapper">
              <div className="v-fab-icon-face v-fab-icon-front">
                <CreditCard size={24} strokeWidth={2.4} color="#ffffff" />
                <span className="v-fab-mini-badge" style={{ color: '#d97706', background: '#ffffff' }}>+</span>
              </div>
            </div>
          </button>
        </div>

        {/* Mobile Action Menu Sheet (Rendered via Portal to eliminate clipping & transform bugs) */}
        {isMobile && activeMenuCard && createPortal(
          <div className="card-mobile-menu-portal" role="dialog" aria-modal="true" aria-label="Card Options">
            <div
              className="card-menu-backdrop"
              onClick={() => setActiveMenuId(null)}
            />
            <div
              className="notes-dropdown-menu card-mobile-action-sheet animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              {renderCardActionMenu(activeMenuCard, true)}
            </div>
          </div>,
          document.body
        )}
      </div>
    </MainLayout>
  );
}
